// GtConfirm — the house confirmation dialogue.
//
// Replaces window.confirm and window.prompt, which are unstyleable browser
// chrome and look nothing like the rest of the app. Imperative on purpose: a
// call site reads `if (!(await gtConfirm({...}))) return;`, a one-line swap for
// the native call, with no new state threaded through the screen.
//
// requireWord renders a typed confirmation (ERASE, DELETE) and keeps the
// action disabled until it matches exactly.
import { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';

function ConfirmDialog({ opts, onClose }) {
  const { title, message, confirmLabel, cancelLabel, tone, requireWord } = opts;
  const [typed, setTyped] = useState('');
  const inputRef = useRef(null);
  const okRef = useRef(null);

  const ready = !requireWord || typed.trim() === requireWord;

  useEffect(() => {
    const el = requireWord ? inputRef.current : okRef.current;
    if (el) el.focus();
  }, [requireWord]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); onClose(false); }
      else if (e.key === 'Enter' && ready) { e.preventDefault(); onClose(true); }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [ready, onClose]);

  const paras = (Array.isArray(message) ? message : [message]).filter(Boolean);

  return (
    <div
      className="gtc-modal-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(false); }}
    >
      <div className="gtc-modal gtc-confirm" role="dialog" aria-modal="true" aria-label={title}>
        <div className="gtc-modal-head">{title}</div>
        <div className="gtc-modal-body">
          {paras.map((p, i) => <p key={i} className="gtc-confirm-p">{p}</p>)}
          {requireWord && (
            <label className="gtc-field">
              <span>Type {requireWord} to confirm</span>
              <input
                ref={inputRef}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                spellCheck="false"
              />
            </label>
          )}
        </div>
        <div className="gtc-modal-foot">
          <button type="button" className="gtc-btn-ghost" onClick={() => onClose(false)}>
            {cancelLabel}
          </button>
          <button
            type="button"
            ref={okRef}
            className={tone === 'danger' ? 'gtc-danger' : 'gtc-primary'}
            disabled={!ready}
            onClick={() => onClose(true)}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function gtConfirm(options) {
  const opts = {
    title: 'Are you sure?',
    message: '',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    tone: 'default',
    requireWord: null,
    ...(options || {}),
  };

  return new Promise((resolve) => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    let done = false;

    const close = (result) => {
      if (done) return;
      done = true;
      // Unmount on a later tick. React refuses to unmount a root while it is
      // still rendering the event that triggered the close.
      setTimeout(() => { root.unmount(); host.remove(); }, 0);
      resolve(result);
    };

    root.render(<ConfirmDialog opts={opts} onClose={close} />);
  });
}

export default gtConfirm;
